import React, { useEffect, useRef, useState } from "react";
import { Avatar, Badge, Button, Card, Drawer, Empty, Input, List, Modal, Space, Spin, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, DeleteOutlined, FileTextOutlined, LeftCircleOutlined, RightCircleOutlined, SearchOutlined, ShoppingCartOutlined, ShoppingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import ChatImagePicker from "components/pickers/image/ChatImagePicker";
import { formatCurrency } from "utils/number";

const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const normalizeURL = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const extractURLs = (text = "") => {
  const matches = String(text || "").match(URL_PATTERN) || [];
  const urls = matches.map((item) => normalizeURL(item)).filter(Boolean);
  return [...new Set(urls)];
};

const renderTextWithLinks = (text = "") => {
  const source = String(text || "");
  const parts = source.split(URL_PATTERN);
  return parts.map((part, index) => {
    if (!part) return null;
    const isLink = URL_PATTERN.test(part);
    URL_PATTERN.lastIndex = 0;
    if (!isLink) return <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>;
    const href = normalizeURL(part);
    return <a key={`${index}-${href}`} href={href} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>{part}</a>;
  });
};

const getInitials = (text = "") => {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "KH";
  }
  return words.slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("");
};

const formatTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
};

const formatTimeGroup = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const shouldShowTimeGroup = (list = [], index = 0) => {
  if (index <= 0) {
    return true;
  }

  const current = new Date(list[index]?.create_on || 0).getTime();
  const previous = new Date(list[index - 1]?.create_on || 0).getTime();
  if (!current || !previous) {
    return false;
  }

  return Math.abs(current - previous) >= 15 * 60 * 1000;
};

const getImageAttachments = (attachments = []) =>
  (attachments || []).filter((item) => item?.type === "image" && item?.url);

const getNonImageAttachments = (attachments = []) =>
  (attachments || []).filter((item) => !(item?.type === "image" && item?.url));

const resolveShippingAddress = (meta = {}) => {
  if (!meta || typeof meta !== "object") {
    return "-";
  }

  if (typeof meta.shipping_address === "string") {
    return String(meta.shipping_address || "").trim() || "-";
  }

  const value = meta?.shipping_address?.address || meta?.shipping_address?.full_address || meta?.customer_address || meta?.address || "";
  return String(value || "").trim() || "-";
};

const renderAttachment = (att, { onPreviewImage, onOpenOrderDetail } = {}) => {
  if (!att) {
    return null;
  }

  if (att.type === "image" && att.url) {
    return (
      <div style={{ marginTop: 8 }}>
        <img
          src={att.url}
          alt={att.title || "chat-image"}
          style={{ maxWidth: 240, width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 12, border: "1px solid rgba(148,163,184,0.25)", cursor: "zoom-in" }}
          onClick={() => onPreviewImage?.(att.url)}
        />
      </div>
    );
  }

  if (att.type === "product") {
    return (
      <div style={{ marginTop: 8, borderRadius: 12, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", padding: 10 }}>
        {!!att.url && (
          <img
            src={att.url}
            alt={att.title || "product"}
            style={{ width: "100%", maxHeight: 130, objectFit: "cover", borderRadius: 10, marginBottom: 8 }}
          />
        )}
        <div style={{ fontWeight: 700, marginBottom: 4 }}><ShoppingOutlined /></div>
        <div>{att.title || att?.meta?.product_name || "Sản phẩm được chia sẻ"}</div>
      </div>
    );
  }

  if (att.type === "order" || att.type === "invoice") {
    const isInvoice = att.type === "invoice";
    const orderID = String(att?.meta?.order_id || "").trim();
    const totalItem = Number(att?.meta?.total_item || 0) || 0;
    const totalAmount = Number(att?.meta?.total_amount || 0) || 0;
    const shippingAddress = resolveShippingAddress(att?.meta || {});
    return (
      <div style={{ marginTop: 8, borderRadius: 12, background: isInvoice ? "rgba(37,99,235,0.1)" : "rgba(249,115,22,0.1)", border: `1px solid ${isInvoice ? "rgba(37,99,235,0.35)" : "rgba(249,115,22,0.35)"}`, padding: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{isInvoice ? <FileTextOutlined /> : <ShoppingCartOutlined />}</div>
        <div>{att.title || `#${orderID}`}</div>
        {!!att?.meta?.customer_name && <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>Khách: {att.meta.customer_name}</div>}
        {isInvoice && (
          <>
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>Tổng sản phẩm: {totalItem}</div>
            <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9 }}>Tổng số tiền: {formatCurrency(totalAmount)}</div>
            <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9 }}>Địa chỉ giao hàng: {shippingAddress}</div>
            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={() => onOpenOrderDetail?.(orderID)}
                style={{ borderRadius: 999 }}
                disabled={!orderID}
              >
                Xem
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  if (att.type === "cart") {
    const totalItem = Number(att?.meta?.total_item || 0) || 0;
    const totalAmount = Number(att?.meta?.total_amount || 0) || 0;
    return (
      <div style={{ marginTop: 8, borderRadius: 12, background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.28)", padding: 10 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}><ShoppingCartOutlined /></div>
        <div>{att.title || "Giỏ hàng hiện tại"}</div>
        {!!att?.meta?.customer_name && <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>Khách: {att.meta.customer_name}</div>}
        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.9 }}>Số sản phẩm: {totalItem}</div>
        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9 }}>Tổng số tiền: {formatCurrency(totalAmount)}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>
      [{att.type}] {att.title || att.url || "Đính kèm"}
    </div>
  );
};

const ChatMobile = ({ viewModel }) => {
  const navigate = useNavigate();
  const {
    loading,
    messageLoading,
    sending,
    threadOptions,
    selectedThread,
    messages,
    content,
    keyword,
    setKeyword,
    setContent,
    onSearch,
    onSelectThread,
    onSend,
    linkPreviews,
    attachments,
    typingText,
    chatImagePickerValue,
    onChangeChatImagePicker,
    onRemoveAttachment,
    attachmentPickerOpen,
    attachmentPickerType,
    attachmentPickerKeyword,
    attachmentPickerLoading,
    attachmentPickerOptions,
    setAttachmentPickerKeyword,
    openAttachmentPicker,
    closeAttachmentPicker,
    loadAttachmentPickerData,
    onPickAttachment,
  } = viewModel;

  const [chatOpen, setChatOpen] = useState(false);
  const messageListRef = useRef(null);
  const messageEndRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const lastStaffMessage = [...messages].reverse().find((item) => item?.sender_type === "shop_staff");

  const previewImage = previewImages[previewIndex] || "";

  const openImagePreview = (images = [], index = 0) => {
    if (!Array.isArray(images) || images.length === 0) return;
    setPreviewImages(images);
    setPreviewIndex(index);
  };

  const closeImagePreview = () => {
    setPreviewImages([]);
    setPreviewIndex(0);
  };

  const showPrevImage = () => {
    if (previewImages.length <= 1) return;
    setPreviewIndex((prev) => (prev - 1 + previewImages.length) % previewImages.length);
  };

  const showNextImage = () => {
    if (previewImages.length <= 1) return;
    setPreviewIndex((prev) => (prev + 1) % previewImages.length);
  };

  const handleSelectThread = (item) => {
    onSelectThread(item);
    setChatOpen(true);
  };

  const handleCloseChat = () => {
    setChatOpen(false);
  };

  const onOpenOrderDetail = (orderID) => {
    const id = String(orderID || "").trim();
    if (!id) {
      return;
    }
    navigate(`/order/${id}`);
  };

  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;
    const onScroll = () => {
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      isNearBottomRef.current = distanceToBottom <= 80;
    };
    onScroll();
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [selectedThread?.id]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [selectedThread?.id]);

  useEffect(() => {
    if (!isNearBottomRef.current) return;
    messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const customerName = selectedThread?.customer_name || selectedThread?.customer_phone || "Khách hàng";

  return (
    <>
      <div style={{ background: "linear-gradient(180deg, #f5f7fb 0%, #eef3f9 100%)", borderRadius: 24, padding: 12 }}>
        <Card title={<Typography.Text strong>Hộp thư khách hàng</Typography.Text>} style={{ borderRadius: 22 }} styles={{ body: { padding: 14 } }}>
          <Space style={{ width: "100%", marginBottom: 10 }}>
            <Input
              placeholder="Tìm khách hàng"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={onSearch}
              size="large"
              style={{ borderRadius: 14 }}
            />
            <Tooltip title="Tìm">
              <Button onClick={onSearch} size="large" style={{ borderRadius: 14 }} icon={<SearchOutlined />} />
            </Tooltip>
          </Space>
          <List
            loading={loading}
            dataSource={threadOptions}
            locale={{ emptyText: <Empty description="Chưa có hội thoại" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            renderItem={(item) => {
              const active = selectedThread?.id === item.id;
              return (
                <List.Item
                  onClick={() => handleSelectThread(item)}
                  style={{
                    cursor: "pointer",
                    background: active ? "linear-gradient(135deg, #e8f1ff 0%, #f3f7ff 100%)" : "#fff",
                    borderRadius: 18,
                    padding: 12,
                    marginBottom: 8,
                    border: active ? "1px solid #b7d0ff" : "1px solid #eef2f7",
                  }}
                >
                  <List.Item.Meta
                    avatar={<Avatar style={{ backgroundColor: active ? "#1677ff" : "#cbd5e1" }}>{getInitials(item.customer_name || item.customer_phone || "Khách hàng")}</Avatar>}
                    title={<Typography.Text strong>{item.customer_name || item.customer_phone || "Khách hàng"}</Typography.Text>}
                    description={
                      <>
                        <Typography.Paragraph ellipsis={{ rows: 1 }} style={{ marginBottom: 2, color: "#64748b" }}>
                          {item.last_message || "Chưa có tin nhắn"}
                        </Typography.Paragraph>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTime(item.last_message_time)}</Typography.Text>
                      </>
                    }
                  />
                  {!!item.unread_shop && <Badge count={item.unread_shop} color="#1677ff" />}
                </List.Item>
              );
            }}
          />
        </Card>
      </div>

      <Drawer
        placement="right"
        width="100%"
        open={chatOpen}
        onClose={handleCloseChat}
        closable={false}
        destroyOnClose={false}
        styles={{
          body: { padding: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" },
          header: { padding: "10px 12px", borderBottom: "1px solid #eef2f7" },
        }}
        title={
          selectedThread ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleCloseChat} style={{ borderRadius: 999 }} />
              <Avatar size={36} style={{ backgroundColor: "#1677ff", flexShrink: 0 }}>{getInitials(customerName)}</Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography.Text strong ellipsis style={{ display: "block" }}>{customerName}</Typography.Text>
              </div>
            </div>
          ) : null
        }
      >
        {selectedThread && (
          <>
            <div ref={messageListRef} style={{ flex: 1, overflowY: "auto", padding: 14, background: "linear-gradient(180deg, #f6f8fc 0%, #edf2f7 100%)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {messageLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                    <Spin tip="Đang tải tin nhắn..." />
                  </div>
                ) : (
                  <>
                    {messages.length === 0 && <Empty description="Chưa có tin nhắn" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    {messages.map((msg, index) => {
                  const isMine = msg.sender_type === "shop_staff";
                  const senderLabel = isMine ? "Bạn" : msg.sender_name || customerName;
                  const showSeen = isMine && msg.id === lastStaffMessage?.id && Number(selectedThread?.unread_customer || 0) === 0;
                  const imageAttachments = getImageAttachments(msg.attachments || []);
                  const nonImageAttachments = getNonImageAttachments(msg.attachments || []);
                  return (
                    <React.Fragment key={msg.id}>
                      {shouldShowTimeGroup(messages, index) && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <Typography.Text type="secondary" style={{ fontSize: 12, background: "#fff", borderRadius: 999, padding: "3px 10px", boxShadow: "0 8px 20px rgba(15,23,42,0.08)" }}>
                            {formatTimeGroup(msg.create_on)}
                          </Typography.Text>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start" }}>
                        <div style={{ display: "flex", flexDirection: isMine ? "row-reverse" : "row", alignItems: "flex-end", gap: 8, maxWidth: "94%" }}>
                          {!isMine && <Avatar size={30} style={{ backgroundColor: "#cbd5e1" }}>{getInitials(senderLabel)}</Avatar>}
                          <div>
                            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4, textAlign: isMine ? "right" : "left" }}>{senderLabel} {formatTime(msg.create_on)}</div>
                            <div style={{ maxWidth: "100%", padding: "11px 14px", borderRadius: isMine ? "18px 18px 8px 18px" : "18px 18px 18px 8px", background: isMine ? "#1677ff" : "#fff", color: isMine ? "#fff" : "#1f2937", boxShadow: "0 10px 24px rgba(15,23,42,0.08)" }}>
                              {msg.content && <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{renderTextWithLinks(msg.content)}</div>}
                              {extractURLs(msg.content || "").map((url) => {
                                const preview = linkPreviews?.[url];
                                if (!preview) return null;
                                return (
                                  <a key={"preview-" + msg.id + "-" + url} href={preview.url || url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
                                    <div style={{ marginTop: 10, borderRadius: 12, background: isMine ? "rgba(255,255,255,0.2)" : "#f8fafc", border: "1px solid rgba(148,163,184,0.28)", overflow: "hidden" }}>
                                      {preview.image && <img src={preview.image} alt={preview.title || "preview"} style={{ width: "100%", maxHeight: 150, objectFit: "cover" }} />}
                                      <div style={{ padding: 10 }}>
                                        <div style={{ fontWeight: 700, marginBottom: 4 }}>{preview.title || preview.url || url}</div>
                                        {!!preview.description && <div style={{ fontSize: 12, opacity: 0.85 }}>{preview.description}</div>}
                                        <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>{preview.site_name || preview.url || url}</div>
                                      </div>
                                    </div>
                                  </a>
                                );
                              })}
                              {imageAttachments.length > 0 && (
                                <div style={{ marginTop: msg.content ? 10 : 0, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}>
                                  {imageAttachments.map((att, imageIndex) => {
                                    const imageUrls = imageAttachments.map((img) => img.url).filter(Boolean);
                                    return (
                                      <img
                                        key={att.id || (msg.id + "-img-" + imageIndex)}
                                        src={att.url}
                                        alt={att.title || "chat-image"}
                                        style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", borderRadius: 10, cursor: "zoom-in", border: "1px solid rgba(148,163,184,0.28)" }}
                                        onClick={() => openImagePreview(imageUrls, imageIndex)}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                              {nonImageAttachments.map((att) => (
                                <div key={att.id}>{renderAttachment(att, { onPreviewImage: (url) => openImagePreview([url], 0), onOpenOrderDetail })}</div>
                              ))}
                            </div>
                            {showSeen && <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, textAlign: "right" }}>Đã xem</div>}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                    })}
                  </>
                )}
                <div ref={messageEndRef} />
              </div>
            </div>

            <div style={{ padding: 12, borderTop: "1px solid #eef2f7", background: "#fff", flexShrink: 0 }}>
              {!!typingText && <div style={{ marginBottom: 8, fontSize: 12, color: "#64748b" }}>{typingText}</div>}
              {attachments.length > 0 && (
                <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                  {attachments.map((att, idx) => (
                    <div key={att.type + "-" + idx} style={{ minWidth: 180, maxWidth: 220, borderRadius: 14, background: "#fff", border: "1px solid #e2e8f0", padding: 8 }}>
                      {renderAttachment(att, { onPreviewImage: (url) => openImagePreview([url], 0), onOpenOrderDetail })}
                      <div style={{ marginTop: 4, textAlign: "right" }}>
                        <Tooltip title="Xóa">
                          <Button size="small" type="text" danger onClick={() => onRemoveAttachment(idx)} icon={<DeleteOutlined />} />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, overflowX: "auto", paddingBottom: 2 }}>
                {(() => {
                  const actionButtonStyle = { borderRadius: 999, minWidth: 38, width: 38, height: 38, padding: 0 };
                  return (
                    <>
                <Tooltip title="Gửi sản phẩm">
                  <Button onClick={() => openAttachmentPicker("product")} style={actionButtonStyle} icon={<ShoppingOutlined />} />
                </Tooltip>
                <Tooltip title="Gửi đơn hàng">
                  <Button onClick={() => openAttachmentPicker("invoice")} style={actionButtonStyle} icon={<FileTextOutlined />} />
                </Tooltip>
                <Tooltip title="Gửi giỏ hàng hiện tại">
                  <Button onClick={() => openAttachmentPicker("cart")} style={actionButtonStyle} icon={<ShoppingCartOutlined />} />
                </Tooltip>
                <Tooltip title="Gửi ảnh">
                  <div>
                    <ChatImagePicker
                      value={chatImagePickerValue}
                      onChange={onChangeChatImagePicker}
                      maxPhotos={10}
                      buttonSize="middle"
                      buttonStyle={actionButtonStyle}
                      counterTextStyle={{ fontSize: 13 }}
                    />
                  </div>
                </Tooltip>
                    </>
                  );
                })()}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", background: "#fff", border: "1px solid #eef2f7", borderRadius: 22, padding: 10, boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  placeholder="Nhập nội dung phản hồi"
                  value={content}
                  variant="borderless"
                  onChange={(e) => setContent(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                />
                <Button type="primary" loading={sending} onClick={onSend} style={{ borderRadius: 999 }}>Gửi</Button>
              </div>
            </div>

            <Modal
              title={
                attachmentPickerType === "product"
                  ? <Space><ShoppingOutlined /><span>Sản phẩm</span></Space>
                  : attachmentPickerType === "cart"
                    ? <Space><ShoppingCartOutlined /><span>Giỏ hàng hiện tại</span></Space>
                  : <Space><FileTextOutlined /><span>Đơn hàng</span></Space>
              }
              open={attachmentPickerOpen}
              onCancel={closeAttachmentPicker}
              footer={null}
              destroyOnClose
            >
              <Space style={{ width: "100%", marginBottom: 12 }}>
                <Input
                  placeholder={attachmentPickerType === "product" ? "Tìm sản phẩm" : attachmentPickerType === "cart" ? "Giỏ hàng hiện tại của khách" : "Tìm đơn hàng"}
                  value={attachmentPickerKeyword}
                  onChange={(e) => setAttachmentPickerKeyword(e.target.value)}
                  onPressEnter={() => {
                    if (!["product", "cart"].includes(attachmentPickerType)) {
                      loadAttachmentPickerData({ type: attachmentPickerType, info: attachmentPickerKeyword });
                    }
                  }}
                />
                {!['product', 'cart'].includes(attachmentPickerType) && (
                  <Tooltip title="Tìm">
                    <Button onClick={() => loadAttachmentPickerData({ type: attachmentPickerType, info: attachmentPickerKeyword })} icon={<SearchOutlined />} />
                  </Tooltip>
                )}
              </Space>
              <List
                loading={attachmentPickerLoading}
                dataSource={attachmentPickerOptions}
                locale={{ emptyText: <Empty description="Không có dữ liệu" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                renderItem={(item) => (
                  <List.Item
                    onClick={() => onPickAttachment(item.raw)}
                    style={{ cursor: "pointer", borderRadius: 12, border: "1px solid #eef2f7", padding: 10, marginBottom: 8 }}
                  >
                    <List.Item.Meta
                      avatar={item.image ? <Avatar shape="square" size={40} src={item.image} /> : <Avatar>{item.title?.[0] || "#"}</Avatar>}
                      title={item.title}
                      description={attachmentPickerType === "product" ? item.subtitle || "" : (
                        <div style={{ fontSize: 12, color: "#64748b", display: "flex", flexDirection: "column", gap: 2 }}>
                          <div>{attachmentPickerType === 'cart' ? 'Cập nhật: ' : 'Ngày tạo: '}{item.createdAt || "-"}</div>
                          <div>Tổng tiền: {item.totalAmountText || "-"}</div>
                          <div>Số sản phẩm: {item.totalItem || 0}</div>
                          {attachmentPickerType !== 'cart' && <div style={{ wordBreak: "break-word" }}>Địa chỉ giao hàng: {item.shippingAddress || "-"}</div>}
                        </div>
                      )}
                    />
                  </List.Item>
                )}
              />
            </Modal>

            <Modal
              open={!!previewImage}
              footer={null}
              onCancel={closeImagePreview}
              centered
              width="94vw"
              styles={{ body: { padding: 8, textAlign: "center" } }}
              destroyOnClose
            >
              {!!previewImage && (
                <div style={{ position: "relative" }}>
                  <img src={previewImage} alt="Xem ảnh" style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 12 }} />
                  {previewImages.length > 1 && (
                    <>
                      <Button type="primary" shape="circle" size="middle" icon={<LeftCircleOutlined />} onClick={showPrevImage} style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)" }} />
                      <Button type="primary" shape="circle" size="middle" icon={<RightCircleOutlined />} onClick={showNextImage} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)" }} />
                    </>
                  )}
                </div>
              )}
            </Modal>
          </>
        )}
      </Drawer>
    </>
  );
};

export default ChatMobile;
